import { Button, Flex, Spinner, Text, TextInput } from "@instructure/ui";
import { StatusCodes } from "http-status-codes";
import { useTranslation } from "react-i18next";
import React, { useRef, useState } from "react";
import { CourseService } from "../../services/CourseService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { Selector } from "../Selectors/Selector";

const AddContextPopover = ({
  setOpenSection,
  setFilteredCourses,
  setSelectedCourses,
  availableDepartments,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const nameRef = useRef();
  const codeRef = useRef();

  const handleCreateContext = () => {
    if (!nameRef.current.value) {
      setNameError(t("academicHierarchy.emptyContextName"));
      return;
    }
    setLoading(true);
    CourseService.upsertContext(
      nameRef.current.value,
      codeRef.current.value,
      selectedDepartment.title
    )
      .then((context) => {
        setFilteredCourses((prevList) => [...prevList, context]);
        setSelectedCourses((prevList) => [...prevList, context]);
        setOpenSection(false);
        showNotification(
          t("notifications.general.addSuccess", {
            obj: t("notifications.context.obj"),
          })
        );
      })
      .catch((e) => {
        if (e.status === StatusCodes.BAD_REQUEST) {
          setCodeError(t("academicHierarchy.contextCodeAlreadyExists"));
        }
        showNotification(
          t("notifications.general.createError", {
            obj: t("notifications.context.obj"),
          }),
          ERROR
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDepartmentSelect = (departmentId) => {
    setSelectedDepartment(
      availableDepartments.find((dep) => dep.id === departmentId)
    );
  };

  return (
    <>
      <Flex as="form" gap="small" wrap="wrap">
        <Flex.Item>
          <Selector
            id="department-select"
            label={t("academicHierarchy.createContextFor")}
            options={availableDepartments}
            selectHandler={handleDepartmentSelect}
            size="small"
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.name")}</Text>
            }
            ref={nameRef}
            messages={nameError ? [{ text: nameError, type: "error" }] : []}
            placeholder={t("academicHierarchy.contextCreationNamePlaceholder")}
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.code")}</Text>
            }
            ref={codeRef}
            messages={codeError ? [{ text: codeError, type: "error" }] : []}
            placeholder={t("academicHierarchy.contextCreationCodePlaceholder")}
          />
        </Flex.Item>
        <Flex.Item textAlign="end" shouldGrow>
          <Button
            size="small"
            color="primary"
            onClick={handleCreateContext}
            interaction={
              selectedDepartment && !loading ? "enabled" : "disabled"
            }
          >
            {loading ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("academicHierarchy.addContext")
            )}
          </Button>
        </Flex.Item>
      </Flex>
    </>
  );
};

export default AddContextPopover;
